/**
 * selectionAtoms - Atom for the current object/edge selection in the graph explorer.
 *
 * Used by the home page for the details panel and by history/undo to snapshot
 * and restore selection state.
 */

import { atom, type Atom, type PrimitiveAtom, type WritableAtom } from 'jotai';

import type { ObjectSelection } from '../panels/ObjectMetadataPanel';
import { RightPanelExpandedAtom } from './panelAtoms';

/** Current selection (node, edge, or none) for the graph explorer. */
export const ObjectSelectionAtom: PrimitiveAtom<ObjectSelection> & {
	init: ObjectSelection;
} = atom<ObjectSelection>({ kind: 'none' });

/** Derived: whether anything is currently selected. */
export const HasSelectionAtom: Atom<boolean> = atom<boolean>(
	(get) => get(ObjectSelectionAtom).kind !== 'none',
);

/**
 * Write atom that sets the selection AND manages RightPanelExpandedAtom in one
 * atomic write, eliminating the need for a separate useEffect + prevRef pattern.
 *
 * Pass a `skipAutoExpand` option to suppress auto-opening the right panel
 * (used during focus mode entry).
 */
export const SetSelectionAtom: WritableAtom<
	null,
	[
		update: {
			selection: ObjectSelection;
			skipAutoExpand?: boolean;
		},
	],
	void
> & {
	init: null;
} = atom(null, (get, set, update: { selection: ObjectSelection; skipAutoExpand?: boolean }) => {
	const prev = get(ObjectSelectionAtom);
	const { selection, skipAutoExpand } = update;
	set(ObjectSelectionAtom, selection);

	const hadSelection = prev.kind !== 'none';
	const hasSelection = selection.kind !== 'none';

	if (!hasSelection && hadSelection) {
		// Deselection → collapse
		set(RightPanelExpandedAtom, false);
	} else if (hasSelection && selection !== prev && !skipAutoExpand) {
		// New or changed selection → expand (unless suppressed)
		set(RightPanelExpandedAtom, true);
	}
});
